/**
 * @fileoverview Evidence Thumbnail Component with Authenticated Loading
 * @module EvidenceThumbnail
 * 
 * Technical Context:
 * - Fetches presigned S3 URL via authenticated API call
 * - Uses presigned URL as img src (no additional auth needed for S3)
 * - Handles loading and error states gracefully
 */

import React, { useState, useEffect } from 'react';
import { Image, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

interface EvidenceThumbnailProps {
  evidenceId: string;
  fileName: string;
  className?: string;
}

const EvidenceThumbnail: React.FC<EvidenceThumbnailProps> = ({
  evidenceId,
  fileName,
  className = 'w-full h-full object-cover'
}) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchImageUrl = async () => {
      try {
        setLoading(true);
        setError(false);
        
        const response = await apiClient.evidence.getDownloadUrl(evidenceId);
        
        if (isMounted && response.data.success) {
          setImageUrl(response.data.data.downloadUrl);
        } else if (isMounted) {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to fetch evidence thumbnail URL:', err);
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchImageUrl();

    return () => {
      isMounted = false;
    };
  }, [evidenceId]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <Image className="w-5 h-5 text-gray-400" />
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={fileName}
      className={className}
      onError={() => setError(true)}
    />
  );
};

export default EvidenceThumbnail;
